import React, { useEffect, useState } from 'react';
import * as yup from 'yup';
import {
  VStack,
  SimpleGrid,
  FormControl,
  FormLabel,
  Switch,
  Checkbox,
  HStack,
  Button,
  ModalFooter,
  useToast,
  Text,
} from '@chakra-ui/react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation } from '@apollo/client';
import context from '../context';
import Datepicker from '../../Form/Datepicker';
import Input from '../../Form/Input';
import {
  CREATE_PAGE_GROUP,
  REMOVE_GROUP_ELEMENT,
  REMOVE_PAGE_GROUP,
  UPDATE_PAGE_GROUP,
} from '../../../graphQL/mutations';
import { GET_PAGE_DATA } from '../../../graphQL/queries';
import Textarea from '../../Form/Textarea';
import sanitizeString from '../../../utils/sanitizeString';

interface Props {
  onCreated?: (data: any) => void;
}

interface FormData {
  name: string;
  description?: string;
  cols: number;
  countdown: boolean;
  height?: string;
  padding?: number;
  gap?: number;
  vertical_padding?: number;
  horizontal_padding?: number;
  rounded_size?: number;
  width?: string;
  schedule: boolean;
  schedule_start?: Date;
  schedule_end?: Date;
  sincronizarSF?: boolean;
}

const formData = yup.object().shape({
  name: yup.string().nullable(),
  description: yup.string().nullable(),
  schedule: yup.boolean(),
  countdown: yup.boolean(),
  sincronizarSF: yup.boolean(),
  height: yup.string().default('120'),
  padding: yup.number().default(0),
  gap: yup
    .number()
    .typeError('Espaçamento é obrigatório, caso não queira digite 0')
    .default(0),
  vertical_padding: yup
    .number()
    .typeError('Padding é obrigatório, caso não queira digite 0')
    .default(0),
  horizontal_padding: yup
    .number()
    .typeError('Padding é obrigatório, caso não queira digite 0')
    .default(0),
  rounded_size: yup
    .number()
    .typeError('Arredondamento é obrigatório, caso não queira digite 0')
    .default(0),
  cols: yup
    .number()
    .typeError('Colunas é obrigatório')
    .required('Colunas é obrigatório')
    .min(2, 'Colunas deve ser no mínimo 2')
    .max(3, 'Colunas deve ser no máximo 3'),
  schedule_start: yup
    .mixed()
    .when('schedule', {
      is: true,
      then: yup.date().required('Data de início é obrigatória'),
    })
    .optional(),
  schedule_end: yup
    .mixed()
    .when(['schedule', 'schedule_start'], (schedule, schedule_start) => {
      if (!schedule) {
        return yup.date();
      }
      if (schedule_start) {
        return yup
          .date()
          .min(schedule_start, 'Data final deve ser superior a data inicial')
          .required('Data Final é obrigatória');
      }
      return yup.date().required('Data Final é obrigatória');
    }),
});

const Form: React.FC<Props> = ({ onCreated }) => {
  const toast = useToast();

  const { pageGroup, newPageGroupDTO, onPageGroupCreated, onClose, display } =
    React.useContext(context);
  const { formState, handleSubmit, register, control, setValue } =
    useForm<FormData>({
      resolver: yupResolver(formData),
    });

  const [removeGroupElement] = useMutation(REMOVE_GROUP_ELEMENT, {
    refetchQueries: [GET_PAGE_DATA, 'GetPageData'],
  });

  const [createPageGroup, { loading: createLoading, error: createError }] =
    useMutation(CREATE_PAGE_GROUP, {
      refetchQueries: [GET_PAGE_DATA, 'GetPageData'],
    });

  const [updatePageGroup, { loading: updateLoading, error: updateError }] =
    useMutation(UPDATE_PAGE_GROUP, {
      refetchQueries: [GET_PAGE_DATA, 'GetPageData'],
    });

  const [removePageGroup, { loading: deleteLoading, error: deleteError }] =
    useMutation(REMOVE_PAGE_GROUP, {
      refetchQueries: [GET_PAGE_DATA, 'GetPageData'],
    });

  const [schedule, setSchedule] = useState(!!pageGroup?.schedule_end);

  const { errors } = formState;

  useEffect(() => {
    if (createError || updateError || deleteError) {
      toast({
        title: 'Opss!!',
        description:
          createError?.message ||
          updateError?.message ||
          deleteError?.message ||
          'Tivemos um erro, tente novamente mais tarde',
        status: 'error',
        position: 'top-right',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createError, updateError, deleteError]);

  useEffect(() => {
    if (!pageGroup) {
      setValue('vertical_padding', 0);
      setValue('horizontal_padding', 0);
      setValue('rounded_size', 0);
      setValue('cols', 2);
      setValue('height', '120');
      setValue('gap', 2);
      setValue('padding', 0);
      setValue('sincronizarSF', false);
      return;
    }
    if (pageGroup?.schedule_end) {
      setValue('schedule', true);
      setValue('schedule_start', new Date(pageGroup.schedule_start));
      setValue('schedule_end', new Date(pageGroup.schedule_end));
      setValue('countdown', pageGroup.countdown);
    }
    setValue('cols', pageGroup?.cols);
    setValue('name', pageGroup.name);
    setValue('description', pageGroup?.description);
    setValue('vertical_padding', pageGroup?.vertical_padding);
    setValue('horizontal_padding', pageGroup?.horizontal_padding);
    setValue('rounded_size', pageGroup?.rounded_size);
    setValue('height', pageGroup?.height);
    setValue('gap', pageGroup?.gap);
    setValue('padding', pageGroup?.padding);
    // usa redirect_type do grupo como flag de sincronização SF
    setValue('sincronizarSF', pageGroup?.redirect_type === 'sync_sf');
  }, [pageGroup, setValue]);

  const handleForm: SubmitHandler<FormData> = async data => {
    try {
      const sanitizedObj = sanitizeString(data);

      const { schedule: scheduleData, sincronizarSF, ...rest } = sanitizedObj;
      const syncFlag = !!sincronizarSF;

      if (pageGroup) {
        if (pageGroup?.cols > data?.cols) {
          const confirmed = window.confirm(
            'Tem certeza que deseja diminuir o número de colunas? Os banners que não couberem serão excluídos',
          );
          if (confirmed) {
            const index = data?.cols;

            try {
              await removeGroupElement({
                variables: {
                  id: pageGroup?.items[index]?.id,
                },
              });
            } catch (err) {
              //
            }
          }
        }
        await updatePageGroup({
          variables: {
            updatePageGroupInput: {
              ...rest,
              schedule_end: scheduleData ? rest.schedule_end : null,
              padding: Number(data?.padding),
              countdown: scheduleData ? rest.countdown : false,
              id: pageGroup?.id,
              redirect_type: syncFlag ? 'sync_sf' : null,
              schedule_start: scheduleData ? rest.schedule_start : new Date(),
            },
          },
        });
        toast({
          title: 'Sucesso!',
          description: 'Grupo editado com sucesso!!',
          status: 'success',
          position: 'top-right',
        });
      } else {
        const apiData = await createPageGroup({
          variables: {
            createPageGroupInput: {
              ...rest,
              ...newPageGroupDTO,
              padding: Number(data?.padding),
              redirect_type: syncFlag ? 'sync_sf' : null,
              element_display_id: display?.id,
              schedule_start: scheduleData ? rest.schedule_start : new Date(),
            },
          },
        });
        if (onCreated) onCreated(apiData);
        if (onPageGroupCreated) onPageGroupCreated(apiData);
        toast({
          title: 'Sucesso!',
          description: 'Grupo criado com sucesso!!',
          status: 'success',
          position: 'top-right',
        });
      }
    } catch {
      // nada
    }
  };

  const handleDelete = async () => {
    try {
      const confirmed = window.confirm(
        'Tem certeza que deseja excluir o grupo?',
      );
      if (confirmed) {
        await removePageGroup({
          variables: {
            id: pageGroup?.id,
          },
        });
        toast({
          title: 'Sucess!!',
          description: 'Grupo deletado com sucesso',
          status: 'success',
          position: 'top-right',
        });
        if (onClose) onClose();
      }
    } catch {
      // error
    }
  };

  return (
    <>
      <VStack as="form" spacing="8" onSubmit={handleSubmit(handleForm)}>
        <SimpleGrid minChildWidth="200px" spacing="8" w="100%">
          <Input label="Título" error={errors?.name} {...register('name')} />
          <Textarea
            label="Subtítulo"
            rows={1}
            error={errors?.description}
            {...register('description')}
          />
          <Input
            label="Colunas (max 3)"
            type="number"
            isRequired
            max={3}
            error={errors?.cols}
            {...register('cols')}
          />
        </SimpleGrid>
        <Text
          as="h2"
          fontSize="md"
          fontWeight="bold"
          textAlign="left"
          alignSelf="start"
        >
          Personalizar
        </Text>
        <SimpleGrid minChildWidth="100px" spacing="8" w="100%">
          <Input
            label="Altura (px)"
            isRequired
            placeholder="Ex: 150px"
            type="number"
            error={errors?.height}
            {...register('height')}
          />
          <Input
            label="Espaçamento entre banners"
            isRequired
            placeholder="2"
            type="number"
            error={errors?.gap}
            {...register('gap')}
          />
        </SimpleGrid>
        <SimpleGrid minChildWidth="100px" spacing="8" w="100%">
          <Input
            label="Padding Vertical (px)"
            type="number"
            isRequired
            error={errors?.vertical_padding}
            {...register('vertical_padding')}
          />
          <Input
            label="Padding Horizontal (px)"
            type="number"
            isRequired
            error={errors?.horizontal_padding}
            {...register('horizontal_padding')}
          />
          <Input
            label="Arredondamento (px)"
            type="number"
            isRequired
            error={errors?.rounded_size}
            {...register('rounded_size')}
          />
        </SimpleGrid>
        <Checkbox
          alignSelf="flex-start"
          colorScheme="brand"
          {...register('sincronizarSF')}
        >
          Sincronizar SalesForce
        </Checkbox>
        <FormControl display="flex" alignItems="center">
          <FormLabel htmlFor="schedule" mb="0">
            Agendamento
          </FormLabel>
          <Switch
            id="schedule"
            size="lg"
            colorScheme="brand"
            isChecked={schedule}
            {...register('schedule')}
            onChange={() => setSchedule(state => !state)}
          />
        </FormControl>
        {schedule && (
          <>
            <SimpleGrid minChildWidth="200px" spacing="8" w="100%">
              <Datepicker
                isRequired
                control={control}
                {...register('schedule_start')}
                label="Início"
                showTimeSelect
                error={errors?.schedule_start}
              />
              <Datepicker
                isRequired
                control={control}
                {...register('schedule_end')}
                label="Final"
                showTimeSelect
                error={errors?.schedule_end}
              />
            </SimpleGrid>
            <Checkbox
              alignSelf="flex-start"
              colorScheme="brand"
              {...register('countdown')}
            >
              Contagem Regressiva
            </Checkbox>
          </>
        )}
      </VStack>

      <ModalFooter>
        <HStack spacing={4}>
          {!!pageGroup && (
            <Button
              onClick={handleDelete}
              colorScheme="red"
              isLoading={deleteLoading}
            >
              Excluir
            </Button>
          )}
          <Button onClick={onClose}>Fechar</Button>
          <Button
            colorScheme="brand"
            isLoading={createLoading || updateLoading}
            onClick={handleSubmit(handleForm)}
          >
            Salvar
          </Button>
        </HStack>
      </ModalFooter>
    </>
  );
};

export default Form;
